'use client'

import { useLingui } from '@lingui/react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import * as React from 'react'

import { ExternalLink } from '@/components/marketing/fragments/external-link'
import { MENU_LINKS } from '@/components/marketing/marketing-links'
import { MobileMenu } from '@/components/marketing/mobile-menu'
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
  navigationMenuTriggerStyle
} from '@/components/ui/navigation-menu'
import { ThemeToggle } from '@/components/ui/theme-toggle'
import { Routes } from '@/constants/routes'
import { cn } from '@/lib/utils'
import Image from 'next/image'
import Logo from 'public/marketing/logos/group.png'
import { StarBorder } from '../ui/star-border'
import { Switcher } from './switcher-language'
export function Navbar(): React.JSX.Element {
  const pathname = usePathname()
  const { i18n } = useLingui()
  return (
    <section className='sticky inset-x-0 top-0 z-50 border-b bg-background max-sm:py-3 py-4'>
      <div className='container'>
        <nav className='hidden justify-between lg:flex'>
          <div className='flex items-center gap-x-9'>
            <Link href={Routes.Root} className='flex items-center gap-2'>
              <Image
                src={Logo}
                width={377}
                height={84}
                className='max-h-10 w-auto'
                alt='Logo Pione'
              />
            </Link>
            <div className='flex items-center'>
              <NavigationMenu
                style={
                  {
                    ['--radius']: '1rem'
                  } as React.CSSProperties
                }
              >
                <NavigationMenuList>
                  {MENU_LINKS.map((item, index) =>
                    item.items ?
                      <NavigationMenuItem key={index}>
                        <NavigationMenuTrigger
                          data-active={
                            item.items.some((subItem) => pathname.startsWith(subItem?.href || '')) ?
                              ''
                            : undefined
                          }
                          className='rounded-xl text-[15px] font-normal data-[active]:bg-accent'
                        >
                          {' '}
                          {i18n._(item.title)}
                        </NavigationMenuTrigger>
                        <NavigationMenuContent>
                          <ul
                            className={cn(
                              'p-4 list-none',
                              item.items.length > 8 ?
                                'grid grid-cols-2 gap-2 w-[768px] max-h-[70vh] overflow-y-auto'
                              : 'grid grid-cols-1 w-96 max-h-[70vh] overflow-y-auto'
                            )}
                          >
                            {item.items.map((subItem, subIndex) => (
                              <li key={subIndex}>
                                <NavigationMenuLink asChild>
                                  <Link
                                    href={subItem.href || '#'}
                                    target={subItem.external ? '_blank' : undefined}
                                    rel={subItem.external ? 'noopener noreferrer' : undefined}
                                    className='group flex select-none flex-row items-center gap-4 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground'
                                  >
                                    <div className='flex size-10 shrink-0 items-center justify-center rounded-xl border bg-background text-muted-foreground transition-colors group-hover:text-foreground'>
                                      {subItem.icon}
                                    </div>
                                    <div>
                                      <div className='text-sm font-medium'>
                                        {i18n._(subItem.title)}
                                        {subItem.external && (
                                          <ExternalLink className='-mt-2 ml-1 inline text-muted-foreground' />
                                        )}
                                      </div>
                                      <p className='text-sm leading-snug text-muted-foreground'>
                                        {i18n._(subItem.description)}
                                      </p>
                                    </div>
                                  </Link>
                                </NavigationMenuLink>
                              </li>
                            ))}
                          </ul>
                        </NavigationMenuContent>
                      </NavigationMenuItem>
                    : <NavigationMenuItem key={index}>
                        <NavigationMenuLink
                          asChild
                          active={pathname.startsWith(item.href)}
                          className={cn(
                            navigationMenuTriggerStyle(),
                            'rounded-xl text-[15px] font-normal data-[active]:bg-accent'
                          )}
                        >
                          <Link
                            href={item.href || '#'}
                            target={item.external ? '_blank' : undefined}
                            rel={item.external ? 'noopener noreferrer' : undefined}
                          >
                            {i18n._(item.title)}
                          </Link>
                        </NavigationMenuLink>
                      </NavigationMenuItem>
                  )}
                </NavigationMenuList>
              </NavigationMenu>
            </div>
          </div>
          <div className='flex items-center gap-2'>
            <Switcher />
            <ThemeToggle className='rounded-xl border-none shadow-none' />
            <StarBorder>
              <Link href={Routes.Contact}>{i18n._('Contact')}</Link>
            </StarBorder>
          </div>
        </nav>
        <MobileMenu className='lg:hidden' />
      </div>
    </section>
  )
}
